import React, {useEffect, useState} from "react";
import {CRITERIA_MODEL, SEARCH_MODEL} from "../../../../../components/table/Model/TableModel";
import {SearchByFilters, SimpleSearchWithId} from "../../../../../reduxToolkit/Search/SearchApi";
import {useDispatch, useSelector} from "react-redux";
import {TABLES} from "../../../../../Apis/Tables";
import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import CustomChip from "../../../../../components/CustomChip";
import {status, STATUS_MODEL} from "../../../../../enums/statusEnum";
import {statusBorderColor, statusColor, statusFontColor} from "../../../../../enums/statusColorEnum";
import DropDownMenuProfile from "../../../../../components/DropDown/DropDownMenuProfile";
import DropDownMenuItem from "../../../../../components/DropDown/DropDownMenuItem";
import {
    CancelIconsOptions,
    CheckIconsOptions,
    CrossIconsOptions,
    DetailsIconsOptions,
    IconAvatarTable,
    IconClockTable,
    IconDeskOfficeTable,
    IconMarkerTable,
    IconScheduleTable,
    LinkIconsOptions
} from "../../../../../components/buttons/IconButtons";
import TableFunction from "../../../../../components/table/TableFunction";
import {getDateFormat, getTimeFormat} from "../../../../../Apis/functions";
import { UpdateVisitorStatusByInvitationId } from "../../../../../reduxToolkit/Invitation/InvitationApi";
import {toast} from "react-toastify";
import {INVITATION_VIEW_TYPE, invitationInTime} from "../Model/Model";
import {DATA_TYPE_MODEL, ORDER_MODEL, SEARCH_OPERATION_MODEL} from "../../../../Modals/Model/Model";
import {fieldModelName} from "../../../../../enums/fieldModelEnum";
import {permissionObj} from "../../../../../Helpers/permission";

/**
 * @author Karen Yunuen Pozos Arias
 * @email kpozos@ibl.mx
 * @title Full Stack Developer
 *
 * IncomingTableApp component manages the logic for the "Incoming Visitor Invitation" table.
 * It handles rendering, search filtering, pagination, row actions (view, accept, reject, cancel),
 * and integrates with Redux for data fetching and updating visitor status.
 *
 * @component
 * @param {Array<string>} selectedList - List of selected row IDs (required).
 * @param {Function} setSelectedList - Function to update selected row IDs (required).
 * @param {Object} criteria - Current search criteria object (required).
 * @param {Function} setCriteria - Setter for the search criteria object (required).
 * @param {string} searchElement - The current search text input (required).
 * @param {Array<Object>} finalArray - Final array of search filters (used to generate criteria) (required).
 * @param {Function} setSearch - Setter to trigger a new search (required).
 * @param {boolean} modified - Flag indicating that data has changed and the table should refresh (required).
 * @param {Function} setModified - Setter used to update the `modified` flag and trigger data reloads (required).
 * @returns {Object} Object containing all necessary table handlers, renderers, and state values:
 * - `thead`, `tbody`: Rendered table header and body.
 * - `pagination`: JSX pagination controls.
 * - `confirmModal`, `detailsModal`: Modal visibility booleans.
 * - `confirmFn`: Function to confirm invitation status changes.
 * - `getActionsButtons`: Function to render row-specific dropdown actions.
 * - and other helpers like `handleCheckboxChange`, `handleDeleteAll`, etc.
 *
 */


export const  IncomingTableApp = ({
    selectedList,
    setSelectedList,
    criteria,
    setCriteria,
    searchElement,
    finalArray,
    setSearch,
    modified,
    setModified
}) => {
    const dispatch = useDispatch();
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const {permission} = useSelector((state) => state.authenticatioauthennSlice);
    const userId = useSelector((state) => state.authenticatioauthennSlice.getUserByIdAtEntry.userId);
    const [isAllChecked, setIsAllChecked] = useState(false);
    const [simpleSearch, setSimpleSearch] = useState(false); // Simple search flag
    const tableId = "tbl_cta_incoming_invitation"; // Set the table ID for identification
    const [results, setResults] = useState(null); // Store the search results
    const searchId = 'txt_af_search_incoming_invitation';
    const option = TABLES.USER_INVITATION;
    const [confirmModal, setConfirmModal] = useState(null);
    const [action, setAction] = useState(null);
    const [detailsModal, setDetailsModal] = useState(null);
    const [itemSelected, setItemSelected] = useState(null);
    const [invitationLinkModal, setInvitationLinkModal] = useState(false);

    /**
     * Generates the table header columns.
     * @returns {React.ReactNode[]} Array of header cells.
     */
    const getThead = () => {
        return [
            <CustomTh tableId={tableId} classes="first_head">{t('host')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('access_zone')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('start_date')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('end_date')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('visitor_status')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('status')}</CustomTh>,
        ];
    };
    /**
     * Generates the table body rows based on fetched data.
     * @returns {{ body: React.ReactNode[], data: Object[] }} Rendered table rows and raw data.
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0) return {
            body: [],
            data: []
        };

        let body = results?.content?.map((item, index) => {
            let fullName = (`${item?.hostName} ${item?.hostLastName} ${item?.hostSecondLastName? item?.hostSecondLastName: ""}`);
            let visitorStatusId = item?.visitorStatusId;
            let statusId = item?.statusId;

            return (
                <>
                    <CustomTd tableId={tableId} classes={"first-column "}>
                            <IconAvatarTable className={"mr-8px mb-1"}/>
                            {fullName}
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        {item?.rootFatherZoneName &&
                        <div className={"d-flex"}>
                            <IconMarkerTable className={"mr-8px mb-1"}/>
                            {item?.rootFatherZoneName}
                        </div>
                        }

                        <div className={"d-flex"}>
                            {item?.rootFatherZoneName ?  <IconDeskOfficeTable className={"mr-8px mb-1"}/> : <IconMarkerTable className={"mr-8px mb-1"}/>}
                            {item?.zoneName}
                        </div>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                            <div>
                                <IconScheduleTable className={"mr-8px mb-1 icon-color-general-950"}/>
                                {getDateFormat(item?.startDate,false)}
                            </div>
                           <div>
                               <IconClockTable className={"mr-8px mb-1 icon-color-general-950"}/>
                               {getTimeFormat(item?.startDate)}
                           </div>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <div>
                            <IconScheduleTable className={"mr-8px mb-1 icon-color-general-950"}/>
                            {getDateFormat(item?.endDate,false)}
                        </div>
                        <div>
                            <IconClockTable className={"mr-8px mb-1 icon-color-general-950"}/>
                            {getTimeFormat(item?.endDate)}
                        </div>
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomChip legend={t(status[visitorStatusId])}
                                    borderColor={statusBorderColor[visitorStatusId]}
                                    fontColor={statusFontColor[visitorStatusId]}
                                    background={statusColor[visitorStatusId]}
                                    variant={"large"}
                        />
                    </CustomTd>
                    <CustomTd tableId={tableId}>
                        <CustomChip legend={t(status[statusId])}
                                    borderColor={statusBorderColor[statusId]}
                                    fontColor={statusFontColor[statusId]}
                                    background={statusColor[statusId]}
                                    variant={"large"}
                        />
                    </CustomTd>
                </>
            );
        });

        return {
            body: body, // Return the generated table rows
            data: results?.content // Return the data associated with the rows
        };
    };


    /**
     * Renders dropdown action buttons per table row based on user permissions.
     * @param {Object} item - Current table row data.
     * @param {number} index - Index of the row.
     * @returns {React.ReactNode} Dropdown menu with actions.
     */
    const getActionsButtons = (item, index) => {

        return <>
            <DropDownMenuProfile
                openMenu={openMenu}
                handleMenuToggle={handleMenuToggle}
                menuId={`menu-${item.id}`} // Unique menu ID for each contractor
            >
                <DropDownMenuItem
                    id={"btn_ita_details_" + item?.id}
                    index={index}
                    handleClose={handleClose}
                    name={t('details')}
                    click={() => {
                        setSelectedList([item?.id]);
                        setItemSelected(item);
                        setDetailsModal(true);
                    }}
                    icon={<DetailsIconsOptions/>}
                />

                { (DEV || permission.includes(permissionObj?.WEB_VISITOR_INVITATION_MANAGE)) && <>
                    { item?.visitorStatusId === STATUS_MODEL.UNATTENDED
                        && item?.statusId !== STATUS_MODEL.CANCELED
                        && invitationInTime(item?.endDate)
                        && <>
                        <DropDownMenuItem
                            id={"btn_ita_accept_" + item?.id}
                            index={index}
                            handleClose={handleClose}
                            name={t('accept')}
                            click={() => {
                                setSelectedList([item?.id]); // Set selected item ID for deletion
                                setConfirmModal(true);
                                setAction(STATUS_MODEL.ACCEPTED);
                            }}
                            icon={<CheckIconsOptions/>}
                        />

                        <DropDownMenuItem
                            id={"btn_ita_rejected_" + item?.id}
                            index={index}
                            handleClose={handleClose}
                            name={t('reject')}
                            fontColor={"font-color-error-600"}
                            iconColor={"icon-color-error-code-600"}
                            click={() => {
                                setSelectedList([item?.id]); // Set selected item ID for deletion
                                setConfirmModal(true);
                                setAction(STATUS_MODEL.REJECTED);
                            }}
                            icon={<CrossIconsOptions/>}
                        />
                    </>
                    }

                    {(![STATUS_MODEL.REJECTED, STATUS_MODEL.CANCELED].includes(item?.visitorStatusId) &&
                        item?.statusId !== STATUS_MODEL.CANCELED &&
                        item?.approvalStatusId === STATUS_MODEL.APPROVED) &&
                        invitationInTime(item?.endDate) &&
                            <DropDownMenuItem
                                id={"btn_ita_link_" + item?.id}
                                index={index}
                                handleClose={handleClose}
                                name={t('invitation_link')}
                                click={() => {
                                    setSelectedList([item?.id]);
                                    setItemSelected(item);
                                    setInvitationLinkModal(true);
                                }}
                                icon={<LinkIconsOptions/>}
                            />
                    }

                    { item?.visitorStatusId === STATUS_MODEL.ACCEPTED
                        && item?.statusId !== STATUS_MODEL.CANCELED
                        && invitationInTime(item?.endDate) &&
                        <>
                            <DropDownMenuItem
                                id={"btn_ita_cancel_" + item?.id}
                                index={index}
                                handleClose={handleClose}
                                name={t('cancel')}
                                fontColor={"font-color-error-600"}
                                iconColor={"icon-color-error-code-600"}
                                click={() => {
                                    setSelectedList([item?.id]); // Set selected item ID for deletion
                                    setConfirmModal(true);
                                    setAction(STATUS_MODEL.CANCELED);
                                }}
                                icon={<CancelIconsOptions/>}
                            />
                        </>
                    }
                </>
                }
            </DropDownMenuProfile>
        </>;
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = (page) => {
        const { dataOption: _u, ...userRule } = userRulePayload();
        const { dataOption: _d, ...deletedRule } = deletedRulePayload();
        const body = SEARCH_MODEL.setObject({ page, size: rowsPerPage, model: option, userId, viewType: INVITATION_VIEW_TYPE.INCOMING, key: searchElement });
        setCriteria({ ...body, rootGroup: { dataOption: SEARCH_OPERATION_MODEL.AND, rules: [userRule, deletedRule], subGroups: [] } });
        setSearch(true);
        dispatch(SimpleSearchWithId({ body })).then(({ payload: { data: { data, success } } }) => {
            if (success) setResults(data);
        });
    };

    /**
     * Executes a paginated search request using advanced criteria.
     *
     * Builds the criteria list based on the advanced search (finalArray),
     * appends user criteria, and dispatches the search action through Redux.
     * Updates the results and resets the selected list once the data is returned.
     */
    const getDataAdvancedSearch = (page) => {
        const { dataOption: _u, ...userRule } = userRulePayload();
        const { dataOption: _d, ...deletedRule } = deletedRulePayload();
        const finalTree = finalArray?.length > 0 ? CRITERIA_MODEL.set(finalArray) : null;
        const rootGroup = {
            dataOption: SEARCH_OPERATION_MODEL.AND,
            rules: [userRule, deletedRule],
            subGroups: finalTree ? [finalTree] : [],
        };
        const body = SEARCH_MODEL.setObject({
            page,
            size: rowsPerPage,
            model: option,
            rootGroup
        });
        setCriteria(body);
        dispatch(SearchByFilters({ option, body })).then(({ payload: { data: { data } } }) => {
            setResults(data);
        });
    };

    // Use the TableFunction SubComponents to handle table logic (checkbox selection, pagination, etc.)
    const { thead,tbody, t, handleCheckboxChange, handleDeleteAll, pagination, page, rowsPerPage,
        openMenu, handleMenuToggle, handleClose} = TableFunction({
        getTbody: getTbody,
        getThead: getThead,
        selectedList,
        setSelectedList,
        data: results,
        labelRowsPerPage: 'invitation_per_page',
        hasContainer: true,
        fixedPagination: false,
        setIsAllChecked,
        searchElement,//New
        getData,//New
        modified,//New
        setModified,//New
        getDataAdvancedSearch,//New - apply only with advanced search
        finalArray,//New - apply only with advanced search
    });

    const userRulePayload = () => ({
        dataOption: SEARCH_OPERATION_MODEL.AND,
        fieldType: DATA_TYPE_MODEL.UUID,
        filterKey: fieldModelName.visitor,
        operation: SEARCH_OPERATION_MODEL.EQ,
        sort: ORDER_MODEL.ASC,
        table: option,
        values: [userId],
    });

    const deletedRulePayload = () => ({
        dataOption: SEARCH_OPERATION_MODEL.AND,
        fieldType: DATA_TYPE_MODEL.BOOLEAN,
        filterKey: fieldModelName.deleted,
        operation: SEARCH_OPERATION_MODEL.EQ,
        sort: ORDER_MODEL.ASC,
        table: option,
        values: [false],
    });

    /**
     * Sends request to update the status of a visitor invitation.
     * Called when user accepts, rejects, or cancels an invitation.
     * @param {string[]} ids - List of selected invitation IDs (only first is used).
     */
    const confirmFn = (ids) => {
        dispatch(UpdateVisitorStatusByInvitationId({invitationId: ids[0], statusId: action})).then(
            ({
                 payload: {
                     data: {success},
                 },
             }) => {
                if (success === true) {
                    setModified(true);
                    setSelectedList([]);
                    setIsAllChecked(false);
                    toast.success(t("has_been_updated_successfully"));
                }
            },
        );
    };

    return {
        t,
        DEV,
        userId,
        permission,
        setResults,
        option,
        page,
        rowsPerPage,
        setSimpleSearch,
        tableId,
        searchId,
        isAllChecked,
        thead,
        tbody,
        confirmModal,
        setConfirmModal,
        action,
        setAction,
        detailsModal,
        setDetailsModal,
        itemSelected,
        setItemSelected,
        invitationLinkModal,
        setInvitationLinkModal,
        pagination,
        handleCheckboxChange,
        handleDeleteAll,
        getActionsButtons,
        confirmFn
    };
};
